import { useEffect, useMemo, useState } from 'react';
import { InlineNotification, Search } from '@carbon/react';
import { useSettledValue } from './hooks';
import { useWellSearch } from './use-well-search';
import { WellPickerTable } from './well-picker-table';
import type { PickerCandidate } from './types';

const PAGE_SIZE = 25;

/**
 * The one hole direction a Well to Well vertex can be: a vertical occupies a single
 * surface location, so it stands on the line unambiguously. `VerticalEligibility` gates
 * every vertex on it, so offering anything else offers a choice the cut discards.
 */
const ELIGIBLE_HOLE_DIRECTIONS = ['vertical'];

/** How long the box has to hold still before it is a search. */
const SETTLE_MS = 400;

export interface WellPickerDirectProps {
  /** Wells already on the section, so the results never offer one twice. */
  excludeUwis: string[];
  stagedUwis: string[];
  onStage: (well: PickerCandidate, checked: boolean) => void;
}

/**
 * Looking a well up by name or UWI, wherever it is.
 *
 * Deliberately not distance-limited: a user who knows which well they want should not
 * have to widen a radius until it appears, and a Well to Well section is free to reach
 * across a basin. It is still limited to vertical holes, because that is what a section
 * vertex can be. What it cannot report is distance from the line — there is no line in
 * this question — so that column reads as unknown here rather than as zero.
 *
 * @param {WellPickerDirectProps} props - Component props.
 * @return {JSX.Element} The pane.
 */
export const WellPickerDirect = ({
  excludeUwis,
  stagedUwis,
  onStage,
}: WellPickerDirectProps): JSX.Element => {
  const [text, setText] = useState('');
  const [page, setPage] = useState(1);
  const settled = useSettledValue(text, SETTLE_MS);

  // A new search is a new result set, so the page it lands on is the first one.
  useEffect(() => {
    setPage(1);
  }, [settled]);

  const request = useMemo(
    () => ({
      text: settled.trim(),
      page,
      pageSize: PAGE_SIZE,
      holeDirectionIds: ELIGIBLE_HOLE_DIRECTIONS,
      excludeUwis,
    }),
    [settled, page, excludeUwis]
  );

  const { wells, totalCount, isLoading, error, searched } =
    useWellSearch(request);

  const candidates: PickerCandidate[] = useMemo(
    () =>
      wells.map((well) => ({
        uwi: well.uwi,
        name: well.name,
        operator: well.currentOperator,
        totalDepthFt: well.trueVerticalDepthFeet ?? null,
        spudDate: well.spudDate,
        // No line in this question, so nothing to measure from.
        offsetFt: null,
        lat: well.surfaceHole?.coordinates[1] ?? null,
        lon: well.surfaceHole?.coordinates[0] ?? null,
      })),
    [wells]
  );

  return (
    <>
      <Search
        labelText="Search wells by name or UWI"
        placeholder="Search by name or UWI"
        size="md"
        value={text}
        onChange={({ target: { value } }) => setText(value)}
      />

      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Could not search"
          subtitle={error.message}
        />
      )}

      {/* Nothing of the result after a failure: the notification above has already said
          what happened, and an empty table under it reads as an answer of none. */}
      {searched && !error && (
        <>
          <WellPickerTable
            wells={candidates}
            stagedUwis={stagedUwis}
            onStage={onStage}
            emptyMessage="No wells match that name or UWI."
            loading={isLoading}
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={(next) => setPage(next)}
          />
        </>
      )}
    </>
  );
};
